import { useTheme } from '../context/ThemeContext';
import { getEmail, getPhoneDisplay, getPhoneHref } from '../data/contactDetails';

export default function PrivacyPolicy() {
  const { colors } = useTheme();

  const s = {
    section: {
      padding: '120px 5vw 80px',
      background: colors.bg,
      minHeight: '100vh',
    },
    container: {
      maxWidth: '900px',
      margin: '0 auto',
    },
    title: {
      fontFamily: "'Inter', sans-serif", fontWeight: 700,
      fontSize: 'clamp(2.5rem, 5vw, 4rem)',
      letterSpacing: '2px',
      color: colors.text,
      marginBottom: '2rem',
    },
    lastUpdated: {
      fontSize: '0.9rem',
      color: colors.muted,
      marginBottom: '3rem',
    },
    heading: {
      fontFamily: "'Inter', sans-serif", fontWeight: 700,
      fontSize: '1.5rem',
      letterSpacing: '1px',
      color: colors.text,
      marginTop: '2.5rem',
      marginBottom: '1rem',
    },
    subheading: {
      fontSize: '1rem',
      fontWeight: 600,
      color: colors.text,
      marginTop: '1.5rem',
      marginBottom: '0.8rem',
    },
    text: {
      fontSize: '0.95rem',
      lineHeight: 1.8,
      color: colors.textBody,
      marginBottom: '1rem',
    },
    list: {
      marginLeft: '1.5rem',
      marginBottom: '1rem',
      color: colors.textBody,
    },
    listItem: {
      marginBottom: '0.6rem',
      fontSize: '0.95rem',
      lineHeight: 1.6,
    },
  };

  return (
    <div style={s.section}>
      <div style={s.container}>
        <h1 style={s.title}>Privacy Policy</h1>
        <p style={s.lastUpdated}>Last Updated: April 2026</p>

        <p style={s.text}>
          Deck For Tech Private Limited ("we," "us," "our," or "Company") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services.
        </p>

        <h2 style={s.heading}>1. Information We Collect</h2>
        <p style={s.text}>
          We may collect information about you in a variety of ways. The information we may collect on the Site includes:
        </p>
        <ul style={s.list}>
          <li style={s.listItem}><strong>Personal Data:</strong> Name, email address, phone number, company name, and other contact information you voluntarily provide.</li>
          <li style={s.listItem}><strong>Device Information:</strong> Browser type, IP address, operating system, device type, and referring URLs.</li>
          <li style={s.listItem}><strong>Usage Information:</strong> Pages visited, time spent on pages, links clicked, and other interactions with our website.</li>
          <li style={s.listItem}><strong>Communication Data:</strong> If you contact us, we may keep records of your communications, inquiries, and our responses.</li>
        </ul>

        <h2 style={s.heading}>2. Use of Your Information</h2>
        <p style={s.text}>
          Having accurate information about you permits us to provide you with a smooth, efficient, and customized experience. Specifically, we may use information collected about you via the Site to:
        </p>
        <ul style={s.list}>
          <li style={s.listItem}>Provide, operate, and maintain our website and services</li>
          <li style={s.listItem}>Process service requests and send related information</li>
          <li style={s.listItem}>Send promotional communications (with your consent)</li>
          <li style={s.listItem}>Respond to your inquiries and provide customer support</li>
          <li style={s.listItem}>Monitor and analyze usage patterns and trends</li>
          <li style={s.listItem}>Improve website functionality and user experience</li>
          <li style={s.listItem}>Prevent fraudulent transactions and enhance security</li>
          <li style={s.listItem}>Comply with legal obligations</li>
        </ul>

        <h2 style={s.heading}>3. Disclosure of Your Information</h2>
        <p style={s.text}>
          We may share your information in the following situations:
        </p>
        <ul style={s.list}>
          <li style={s.listItem}><strong>Service Providers:</strong> To vendors, contractors, and service providers who perform services on our behalf and are contractually obligated to keep information confidential.</li>
          <li style={s.listItem}><strong>Legal Requirements:</strong> When required by law or to protect our legal rights, privacy, safety, or property.</li>
          <li style={s.listItem}><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.</li>
          <li style={s.listItem}><strong>Consent:</strong> With your explicit consent for purposes you have agreed to.</li>
        </ul>

        <h2 style={s.heading}>4. Security of Your Information</h2>
        <p style={s.text}>
          We use administrative, technical, and physical security measures to protect your personal information. However, no method of transmission over the Internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal data, we cannot guarantee its absolute security.
        </p>

        <h2 style={s.heading}>5. Contact Information and Cookies</h2>
        <p style={s.text}>
          We may use cookies and similar tracking technologies to enhance your browsing experience. You can control cookie settings through your browser preferences. Please note that some features of our website may not function properly if cookies are disabled.
        </p>

        <h2 style={s.heading}>6. Your Rights</h2>
        <p style={s.text}>
          Depending on your location, you may have the following rights regarding your personal data:
        </p>
        <ul style={s.list}>
          <li style={s.listItem}>Right to access your personal data</li>
          <li style={s.listItem}>Right to correct inaccurate data</li>
          <li style={s.listItem}>Right to request deletion of your data</li>
          <li style={s.listItem}>Right to restrict processing</li>
          <li style={s.listItem}>Right to data portability</li>
          <li style={s.listItem}>Right to opt-out of marketing communications</li>
        </ul>

        <h2 style={s.heading}>7. Children's Privacy</h2>
        <p style={s.text}>
          Our website is not intended for children under 18 years of age. We do not knowingly collect personal information from children. If we become aware that a child has provided us with personal information, we will take steps to delete such information and terminate the child's account.
        </p>

        <h2 style={s.heading}>8. Third-Party Links</h2>
        <p style={s.text}>
          Our website may contain links to third-party websites and services that are not operated by us. This Privacy Policy does not apply to third-party websites, and we are not responsible for their privacy practices. We encourage you to review the privacy policies of any third-party services before providing your personal information.
        </p>

        <h2 style={s.heading}>9. Changes to This Privacy Policy</h2>
        <p style={s.text}>
          We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, and other factors. We will notify you of any material changes by updating the "Last Updated" date at the top of this policy. Your continued use of our website following the posting of revised Privacy Policy means that you accept and agree to the changes.
        </p>

        <h2 style={s.heading}>10. Contact Us</h2>
        <p style={s.text}>
          If you have questions or concerns about this Privacy Policy or our privacy practices, please contact us at:
        </p>
        <div style={{ ...s.list, marginLeft: 0 }}>
          <p style={s.text}>
            <strong>Deck For Tech Private Limited</strong><br />
            Email: <a href={`mailto:${getEmail()}`} style={{ color: colors.accent }}>{getEmail()}</a><br />
            Phone: <a href={`tel:${getPhoneHref()}`} style={{ color: colors.accent }}>{getPhoneDisplay()}</a>
          </p>
        </div>

        <p style={{ ...s.text, marginTop: '3rem', fontStyle: 'italic', color: colors.muted }}>
          This privacy policy is provided for informational purposes. For legal advice regarding your specific situation, please consult with a qualified legal professional.
        </p>
      </div>
    </div>
  );
}
